import Image from "next/legacy/image"
import { useCallback, useEffect, useRef, useState } from "react"
import { FormProvider, useForm, useFormContext } from "react-hook-form"
import { completeSwishOrder, createSwishOrder } from "@/lib/api/swish"
import { useTemplate } from "@/lib/helpers/template"
import { AmountItem } from "@/components/checkout-form/amount/amount"
import CustomValue from "@/components/checkout-form/amount/customValue"
import swedbankPayStyles from "@/components/checkout-form/payment/gateways/swedbankPayStyles"
import Button from "@/common/button/button"
import CheckmarkCircle from "@/common/partials/checkmarkCircle"
import { Spinner } from "@/components/common/loading/spinner"
import { usePropsObject } from "@/lib/contexts/appContext"
import { Terms } from "@/components/checkout-form/payment/payment"
import { CheckoutProvider } from "@/components/checkout-form/store"
import Block from "@/components/wrappers/block"
import { pushDataLayer } from "@/lib/helpers/tagManager"
import classNames from "classnames"
import styles from "./swish.module.css"
import ReCAPTCHA from "react-google-recaptcha"

const SWEDBANK_PAY_CONTAINER_ID = "swedbank-pay-seamless-view-page"

export default function Swish({ block }) {
  const {
    image,
    minAmount,
    amounts = [],
    heading,
    description,
    buttonText,
    productId,
    thankYouContent,
    resetButtonText,
    terms
  } = block.attrs

  const [script, setScript] = useState()
  const [orderComplete, setOrderComplete] = useState(false)
  const [loading, setLoading] = useState(false)
  const [tracking, setTracking] = useState(false)
  const payexObjectRef = useRef()
  const template = useTemplate()
  const { theme } = usePropsObject()
  const recaptchaRef = useRef()
  const siteKey = process.env.NEXT_PUBLIC_CAPTCHA_SITE_KEY

  const scriptContainerRef = useCallback(
    (node) => {
      if (!node) {
        return
      }

      let tag = document.createElement("script")
      tag.onload = onPaymentScriptLoad
      tag.src = script

      node.appendChild(tag)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [script]
  )

  const onPaymentScriptLoad = () => {
    payexObjectRef.current =
      window.payex.hostedView.checkout(swedbankPaySettings)
    payexObjectRef.current.open()
  }

  let defaultValue = amounts.find((amount) => amount.isDefault)
  defaultValue = defaultValue || amounts[0]

  const [isCustomValue, setIsCustomValue] = useState(false)

  const methods = useForm({
    defaultValues: {
      amount: defaultValue?.amount ?? 0,
      customValue: minAmount
    },
    mode: "onTouched"
  })

  useEffect(() => {
    setLoading(methods.formState.isSubmitting)
  }, [methods.formState.isSubmitting])

  useEffect(() => {
    methods.setValue("customValue", minAmount)
    methods.trigger("customValue")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCustomValue])

  const onSubmit = async ({ amount, customValue }) => {
    const token = await recaptchaRef.current.executeAsync()
    const result = await createSwishOrder(
      isCustomValue ? +customValue : +amount,
      productId,
      token
    )

    setTracking(result.result.tracking)
    setScript(result.result.script)
  }

  const onPaid = async (data) => {
    const paymentOrderId = data.paymentOrder?.id ?? data.id
    setLoading(true)
    const token = await recaptchaRef.current.executeAsync()
    const status = await completeSwishOrder(paymentOrderId, token)

    if (status === 200) {
      setOrderComplete(true)
      pushDataLayer(tracking)
      setLoading(false)
      setScript(null)
    }
  }

  const resetPayment = () => {
    payexObjectRef.current.cancel()
    setScript(null)
  }

  const resetForm = () => {
    setOrderComplete(false)
    payexObjectRef.current?.close()
    payexObjectRef.current = null
  }

  const swedbankPaySettings = {
    container: SWEDBANK_PAY_CONTAINER_ID,
    style: swedbankPayStyles,
    culture: "sv-SE",
    onPaid,
    onAborted: () => {
      payexObjectRef.current?.close()
      setScript(null)
    }
  }

  const states = {
    amounts,
    isCustomValue,
    setIsCustomValue,
    minAmount
  }

  let spinColor = "rgba(115,175,60,1)"

  const blockClasses = [
    styles.container,
    { "has-image": image },
    styles[template],
    theme?.txtColor === "white" && theme.plateColor === "lime"
      ? "text-black-100"
      : ""
  ]

  if (template === "campaign" && theme) {
    blockClasses.push(
      theme.plateColor ? "plate-" + theme.plateColor : "",
      theme.accentColor ? "accent-" + theme.accentColor : ""
    )
  }

  let buttonTheme = "green"

  switch (template) {
    case "article":
      buttonTheme = "campaign-lime"
      break

    case "campaign":
      buttonTheme = theme.accentColor
      if (!buttonTheme && !theme.plateColor) {
        buttonTheme = "green"
      }
      break

    default:
      break
  }

  if (amounts.length === 0) {
    return null
  }

  const Copy = () => {
    if (script || orderComplete) {
      return null
    }
    return (
      <>
        {heading && <h2>{heading}</h2>}
        {description && <p>{description}</p>}
      </>
    )
  }

  return (
    <Block block={block} width="bleed" className={classNames(blockClasses)}>
      <ReCAPTCHA ref={recaptchaRef} size="invisible" sitekey={siteKey} />
      <div className="inner-wrapper">
        <div className={classNames("form-wrapper", { "has-script": script })}>
          <Copy />
          <FormProvider {...methods} {...states}>
            <form onSubmit={methods.handleSubmit(onSubmit)}>
              {!script && !orderComplete && (
                <>
                  <Amounts />
                  <CheckoutProvider
                    form={{
                      terms: terms
                    }}>
                    <Terms />
                  </CheckoutProvider>
                  <Button
                    theme={buttonTheme}
                    ariaLabel={buttonText}
                    disabled={!methods.formState.isValid}
                    type="submit">
                    {buttonText}
                  </Button>
                </>
              )}
              {script && (
                <>
                  <div
                    id={SWEDBANK_PAY_CONTAINER_ID}
                    ref={scriptContainerRef}
                  />
                  <span className="cancel-payment" onClick={resetPayment}>
                    Avbryt
                  </span>
                </>
              )}

              {orderComplete && (
                <>
                  <div
                    className="prose text-black-100"
                    dangerouslySetInnerHTML={{ __html: thankYouContent }}
                  />
                  {resetButtonText && (
                    <Button
                      ariaLabel={resetButtonText}
                      theme={buttonTheme}
                      className="mt-6"
                      onClick={resetForm}>
                      {resetButtonText}
                    </Button>
                  )}
                </>
              )}
            </form>
          </FormProvider>

          {loading && (
            <div className="loading-wrapper">
              <Spinner spinColor={spinColor} />
            </div>
          )}
        </div>
        {image && (
          <figure>
            <Image
              alt={image.alt}
              src={image.url}
              width={image.width}
              height={image.height}
              sizes="(max-width: 768px) 100vw, 50vw"
              layout="responsive"
            />
          </figure>
        )}
      </div>
    </Block>
  )
}

function Amounts() {
  const { watch, amounts, isCustomValue, setIsCustomValue, minAmount } =
    useFormContext()
  const amount = watch("amount")

  return (
    <>
      <div className="flex flex-wrap my-6 font-bold bg-white border divide-y divide-gray-500 rounded border-black-50 lg:divide-y-0 lg:divide-x">
        {amounts.map((amountObj) => (
          <AmountItem
            id="amountLight"
            key={amountObj.amount}
            amount={amountObj.amount}
            isSelected={amount === amountObj.amount && !isCustomValue}
            onClick={() => setIsCustomValue(false)}
          />
        ))}
      </div>
      <div className="mb-6">
        <CustomValue id="Light" hidden={!isCustomValue} minAmount={minAmount} />
        <label
          htmlFor="customValueLight"
          className={`flex bg-white items-center px-4 py-4 text-lg font-bold border border-black-50 rounded cursor-pointer group ${
            isCustomValue ? "hidden" : ""
          }`}
          onClick={() => setIsCustomValue(true)}>
          <CheckmarkCircle />
          Valfritt
        </label>
      </div>
    </>
  )
}
